import { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import MainLayout from '@/components/layout/MainLayout';
import AuthLayout from '@/components/layout/AuthLayout';
import LoadingScreen from '@/components/common/LoadingScreen';
import ProtectedRoute from '@/components/auth/ProtectedRoute';

// ============================================
// LAZY LOADED PAGES
// ============================================

// Auth
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));

// Dashboard
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));

// Accounting
const AccountsPage = lazy(() => import('@/pages/accounting/AccountsPage'));
const AccountFormPage = lazy(() => import('@/pages/accounting/AccountFormPage'));
const JournalEntriesPage = lazy(() => import('@/pages/accounting/JournalEntriesPage'));
const JournalEntryFormPage = lazy(() => import('@/pages/accounting/JournalEntryFormPage'));
const TaxesPage = lazy(() => import('@/pages/accounting/TaxesPage'));
const TaxFormPage = lazy(() => import('@/pages/accounting/TaxFormPage'));
const FiscalYearsPage = lazy(() => import('@/pages/accounting/FiscalYearsPage'));
const FiscalYearFormPage = lazy(() => import('@/pages/accounting/FiscalYearFormPage'));
const PaymentsPage = lazy(() => import('@/pages/accounting/PaymentsPage'));
const PaymentFormPage = lazy(() => import('@/pages/accounting/PaymentFormPage'));
const ReportsPage = lazy(() => import('@/pages/accounting/AccountingReportsPage'));

// Partners
const PartnersPage = lazy(() => import('@/pages/partners/PartnersListPage'));
const PartnerFormPage = lazy(() => import('@/pages/partners/PartnerFormPage'));
const PartnerDetailPage = lazy(() => import('@/pages/partners/PartnerDetailPage'));

// Inventory
const ProductsPage = lazy(() => import('@/pages/inventory/ProductsListPage'));
const ProductDetailPage = lazy(() => import('@/pages/inventory/ProductDetailPage'));
const ProductFormPage = lazy(() => import('@/pages/inventory/ProductFormPage'));
const CategoriesPage = lazy(() => import('@/pages/inventory/ProductCategoriesPage'));
const CategoryFormPage = lazy(() => import('@/pages/inventory/CategoryFormPage'));
const UnitsPage = lazy(() => import('@/pages/inventory/UnitsPage'));
const WarehousesPage = lazy(() => import('@/pages/inventory/WarehousesPage'));
const WarehouseDetailPage = lazy(() => import('@/pages/inventory/WarehouseDetailPage'));
const WarehouseFormPage = lazy(() => import('@/pages/inventory/WarehouseFormPage'));
const StockMovesPage = lazy(() => import('@/pages/inventory/StockMovementsPage'));

// Sales
const SalesOrdersPage = lazy(() => import('@/pages/sales/SalesOrdersPage'));
const SalesOrderFormPage = lazy(() => import('@/pages/sales/SalesOrderFormPage'));
const SalesInvoicesPage = lazy(() => import('@/pages/sales/SalesInvoicesPage'));
const SalesInvoiceFormPage = lazy(() => import('@/pages/sales/SalesInvoiceFormPage'));
const DeliveriesPage = lazy(() => import('@/pages/sales/DeliveryNotesPage'));
const DeliveryFormPage = lazy(() => import('@/pages/sales/DeliveryFormPage'));

// Purchase
const PurchaseOrdersPage = lazy(() => import('@/pages/purchase/PurchaseOrdersPage'));
const PurchaseOrderFormPage = lazy(() => import('@/pages/purchase/PurchaseOrderFormPage'));
const PurchaseInvoicesPage = lazy(() => import('@/pages/purchase/SupplierInvoicesPage'));
const SupplierInvoiceFormPage = lazy(() => import('@/pages/purchase/SupplierInvoiceFormPage'));
const ReceptionsPage = lazy(() => import('@/pages/purchase/GoodsReceiptsPage'));
const ReceptionFormPage = lazy(() => import('@/pages/purchase/ReceptionFormPage'));

// HR
const EmployeesPage = lazy(() => import('@/pages/hr/EmployeesPage'));
const EmployeeFormPage = lazy(() => import('@/pages/hr/EmployeeFormPage'));
const ContractsPage = lazy(() => import('@/pages/hr/ContractsPage'));
const ContractFormPage = lazy(() => import('@/pages/hr/ContractFormPage'));
const EmployeeContractFormPage = lazy(() => import('@/pages/hr/EmployeeContractFormPage'));
const PositionsPage = lazy(() => import('@/pages/hr/PositionsPage'));
const PositionFormPage = lazy(() => import('@/pages/hr/PositionFormPage'));
const PayslipsPage = lazy(() => import('@/pages/hr/PayrollPage'));
const LeavesPage = lazy(() => import('@/pages/hr/LeavesPage'));
const AttendancePage = lazy(() => import('@/pages/hr/AttendancePage'));
const DepartmentsPage = lazy(() => import('@/pages/hr/DepartmentsPage'));
const DepartmentFormPage = lazy(() => import('@/pages/hr/DepartmentFormPage'));

// POS
const POSPage = lazy(() => import('@/pages/pos/POSPage'));
const POSConfigsPage = lazy(() => import('@/pages/pos/POSSettingsPage'));
const POSSessionsPage = lazy(() => import('@/pages/pos/POSSessionsPage'));
const POSOrdersPage = lazy(() => import('@/pages/pos/POSOrdersPage'));

// Manufacturing
const BOMsPage = lazy(() => import('@/pages/manufacturing/BOMsPage'));
const WorkCentersPage = lazy(() => import('@/pages/manufacturing/WorkCentersPage'));
const ManufacturingOrdersPage = lazy(() => import('@/pages/manufacturing/ManufacturingOrdersPage'));

// Projects
const ProjectsPage = lazy(() => import('@/pages/projects/ProjectsPage'));
const ProjectFormPage = lazy(() => import('@/pages/projects/ProjectFormPage'));
const ProjectDetailPage = lazy(() => import('@/pages/projects/ProjectDetailPage'));
const TasksPage = lazy(() => import('@/pages/projects/TasksPage'));
const TimesheetsPage = lazy(() => import('@/pages/projects/TimesheetsPage'));

// Documents
const DocumentsPage = lazy(() => import('@/pages/documents/DocumentsPage'));

// Settings
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const UsersPage = lazy(() => import('@/pages/settings/UsersPage'));
const CompanySettingsPage = lazy(() => import('@/pages/settings/CompanySettingsPage'));
const GeneralSettingsPage = lazy(() => import('@/pages/settings/GeneralSettingsPage'));

// Not Found
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// ============================================
// APP COMPONENT
// ============================================

function App() {
  const { checkAuth } = useAuthStore();
  const { theme } = useUIStore();
  
  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  
  // Apply theme on mount and change
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
  }, [theme]);
  
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        </Route>
        
        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          
          {/* Accounting */}
          <Route path="/accounting">
            <Route index element={<Navigate to="/accounting/accounts" replace />} />
            <Route path="accounts" element={<AccountsPage />} />
            <Route path="accounts/new" element={<AccountFormPage />} />
            <Route path="accounts/:id" element={<AccountFormPage />} />
            <Route path="accounts/:id/edit" element={<AccountFormPage />} />
            <Route path="journal-entries" element={<JournalEntriesPage />} />
            <Route path="journal-entries/new" element={<JournalEntryFormPage />} />
            <Route path="journal-entries/:id" element={<JournalEntryFormPage />} />
            <Route path="journal-entries/:id/edit" element={<JournalEntryFormPage />} />
            <Route path="taxes" element={<TaxesPage />} />
            <Route path="taxes/new" element={<TaxFormPage />} />
            <Route path="taxes/:id" element={<TaxFormPage />} />
            <Route path="taxes/:id/edit" element={<TaxFormPage />} />
            <Route path="fiscal-years" element={<FiscalYearsPage />} />
            <Route path="fiscal-years/new" element={<FiscalYearFormPage />} />
            <Route path="fiscal-years/:id" element={<FiscalYearFormPage />} />
            <Route path="fiscal-years/:id/edit" element={<FiscalYearFormPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="payments/new" element={<PaymentFormPage />} />
            <Route path="payments/:id" element={<PaymentFormPage />} />
            <Route path="payments/:id/edit" element={<PaymentFormPage />} />
            <Route path="reports" element={<ReportsPage />} />
          </Route>
          
          {/* Partners */}
          <Route path="/partners">
            <Route index element={<PartnersPage />} />
            <Route path="new" element={<PartnerFormPage />} />
            <Route path=":id/edit" element={<PartnerFormPage />} />
            <Route path=":id" element={<PartnerDetailPage />} />
          </Route>
          
          {/* Inventory */}
          <Route path="/inventory">
            <Route index element={<Navigate to="/inventory/products" replace />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="products/new" element={<ProductFormPage />} />
            <Route path="products/:id/edit" element={<ProductFormPage />} />
            <Route path="products/:id" element={<ProductDetailPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="categories/new" element={<CategoryFormPage />} />
            <Route path="categories/:id/edit" element={<CategoryFormPage />} />
            <Route path="units" element={<UnitsPage />} />
            <Route path="warehouses" element={<WarehousesPage />} />
            <Route path="warehouses/new" element={<WarehouseFormPage />} />
            <Route path="warehouses/:id" element={<WarehouseDetailPage />} />
            <Route path="warehouses/:id/edit" element={<WarehouseFormPage />} />
            <Route path="stock-moves" element={<StockMovesPage />} />
          </Route>
          
          {/* Sales */}
          <Route path="/sales">
            <Route index element={<Navigate to="/sales/orders" replace />} />
            <Route path="orders" element={<SalesOrdersPage />} />
            <Route path="orders/new" element={<SalesOrderFormPage />} />
            <Route path="orders/:id" element={<SalesOrderFormPage />} />
            <Route path="orders/:id/edit" element={<SalesOrderFormPage />} />
            <Route path="invoices" element={<SalesInvoicesPage />} />
            <Route path="invoices/new" element={<SalesInvoiceFormPage />} />
            <Route path="invoices/:id" element={<SalesInvoiceFormPage />} />
            <Route path="invoices/:id/edit" element={<SalesInvoiceFormPage />} />
            <Route path="deliveries" element={<DeliveriesPage />} />
            <Route path="deliveries/new" element={<DeliveryFormPage />} />
            <Route path="deliveries/:id" element={<DeliveryFormPage />} />
            <Route path="deliveries/:id/edit" element={<DeliveryFormPage />} />
          </Route>
          
          {/* Purchase */}
          <Route path="/purchase">
            <Route index element={<Navigate to="/purchase/orders" replace />} />
            <Route path="orders" element={<PurchaseOrdersPage />} />
            <Route path="orders/new" element={<PurchaseOrderFormPage />} />
            <Route path="orders/:id" element={<PurchaseOrderFormPage />} />
            <Route path="orders/:id/edit" element={<PurchaseOrderFormPage />} />
            <Route path="invoices" element={<PurchaseInvoicesPage />} />
            <Route path="invoices/new" element={<SupplierInvoiceFormPage />} />
            <Route path="invoices/:id" element={<SupplierInvoiceFormPage />} />
            <Route path="invoices/:id/edit" element={<SupplierInvoiceFormPage />} />
            <Route path="receptions" element={<ReceptionsPage />} />
            <Route path="receptions/new" element={<ReceptionFormPage />} />
            <Route path="receptions/:id" element={<ReceptionFormPage />} />
            <Route path="receptions/:id/edit" element={<ReceptionFormPage />} />
          </Route>
          
          {/* HR */}
          <Route path="/hr">
            <Route index element={<Navigate to="/hr/employees" replace />} />
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="employees/new" element={<EmployeeFormPage />} />
            <Route path="employees/:id" element={<EmployeeFormPage />} />
            <Route path="contracts" element={<ContractsPage />} />
            <Route path="contracts/new" element={<ContractFormPage />} />
            <Route path="contracts/:id" element={<ContractFormPage />} />
            <Route path="contracts/employee/new" element={<EmployeeContractFormPage />} />
            <Route path="contracts/employee/:id" element={<EmployeeContractFormPage />} />
            <Route path="positions" element={<PositionsPage />} />
            <Route path="positions/new" element={<PositionFormPage />} />
            <Route path="positions/:id" element={<PositionFormPage />} />
            <Route path="payslips" element={<PayslipsPage />} />
            <Route path="leaves" element={<LeavesPage />} />
            <Route path="leaves/new" element={<LeavesPage />} />
            <Route path="leaves/:id" element={<LeavesPage />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="departments" element={<DepartmentsPage />} />
            <Route path="departments/new" element={<DepartmentFormPage />} />
            <Route path="departments/:id" element={<DepartmentFormPage />} />
          </Route>
          
          {/* POS */}
          <Route path="/pos">
            <Route index element={<POSPage />} />
            <Route path="terminal/:id" element={<POSPage />} />
            <Route path="configs" element={<POSConfigsPage />} />
            <Route path="sessions" element={<POSSessionsPage />} />
            <Route path="orders" element={<POSOrdersPage />} />
          </Route>
          
          {/* Manufacturing */}
          <Route path="/manufacturing">
            <Route index element={<Navigate to="/manufacturing/orders" replace />} />
            <Route path="boms" element={<BOMsPage />} />
            <Route path="work-centers" element={<WorkCentersPage />} />
            <Route path="orders" element={<ManufacturingOrdersPage />} />
          </Route>
          
          {/* Projects */}
          <Route path="/projects">
            <Route index element={<ProjectsPage />} />
            <Route path="new" element={<ProjectFormPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="timesheets" element={<TimesheetsPage />} />
            <Route path=":id" element={<ProjectDetailPage />} />
            <Route path=":id/edit" element={<ProjectFormPage />} />
            <Route path=":id/tasks" element={<TasksPage />} />
          </Route>
          
          {/* Documents */}
          <Route path="/documents" element={<DocumentsPage />} />
          
          {/* Settings */}
          <Route path="/settings">
            <Route index element={<SettingsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="company" element={<CompanySettingsPage />} />
            <Route path="general" element={<GeneralSettingsPage />} />
          </Route>
        </Route>
        
        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
